import type { Request } from '../types';

// ============================================================================
// Age Thresholds
// ============================================================================
export const AGE_HIGH_THRESHOLD = 30;
export const AGE_MEDIUM_THRESHOLD = 20;

// ============================================================================
// Deduplication Utilities
// ============================================================================

/**
 * Dedupe requests by Opportunity ID.
 *
 * When multiple requests exist for the same opportunity, keeps only the first
 * occurrence. Used for opportunity-level metrics (e.g., summing opportunity
 * revenue without double-counting).
 *
 * @param items - Request items to dedupe
 * @returns Deduplicated array (one request per unique opp_id)
 */
export function dedupeByOppId<T extends { opp_id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter(item => {
    if (seen.has(item.opp_id)) return false;
    seen.add(item.opp_id);
    return true;
  });
}

/**
 * Dedupe requests by Request ID.
 *
 * Removes duplicate request records. The backend should not return duplicates,
 * but this provides safety for true request-level counting.
 *
 * @param items - Request items to dedupe
 * @returns Deduplicated array (one per unique req_id)
 */
export function dedupeByReqId<T extends { req_id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter(item => {
    if (seen.has(item.req_id)) return false;
    seen.add(item.req_id);
    return true;
  });
}

// ============================================================================
// Aggregation Utilities
// ============================================================================

/**
 * Sum a value across requests, deduped by opportunity.
 *
 * Used for opportunity-level metrics (e.g., total GNARR) to avoid counting
 * the same opportunity multiple times when multiple requests exist for it.
 *
 * @param requests - Request array
 * @param getValue - Extractor function
 * @returns Sum of extracted values (deduped by opp_id)
 */
export function sumUniqueByOpp(
  requests: Request[],
  getValue: (r: Request) => number
): number {
  return dedupeByOppId(requests).reduce((sum, r) => sum + (getValue(r) || 0), 0);
}

/**
 * Total GNARR across SC rows, deduped by opportunity ID.
 *
 * Per-SC GNARR is already deduped within a row, but summing rows double-counts an
 * opp worked by several SCs, or one that has both In-Progress and Closed requests.
 * Each total here counts every opp_id once:
 * - ip: across all In-Progress requests
 * - closed: across all Closed requests
 * - total: across the union of both
 *
 * @param rows - SC rows carrying their In-Progress and Closed requests
 * @returns Deduped GNARR totals
 */
export function sumScTotalsGnarr(
  rows: { requests: Request[]; closedRequests: Request[] }[]
): { ip: number; closed: number; total: number } {
  const ipRequests = rows.flatMap(r => r.requests);
  const closedRequests = rows.flatMap(r => r.closedRequests);
  const gnarr = (r: Request) => r.opp_gnarr;
  return {
    ip: sumUniqueByOpp(ipRequests, gnarr),
    closed: sumUniqueByOpp(closedRequests, gnarr),
    total: sumUniqueByOpp([...ipRequests, ...closedRequests], gnarr),
  };
}

/**
 * Sum a value across all requests (no deduplication).
 *
 * Used for request-level metrics where each request should be counted.
 *
 * @param requests - Request array
 * @param getValue - Extractor function
 * @returns Sum of extracted values
 */
export function sumAllRequests(
  requests: Request[],
  getValue: (r: Request) => number
): number {
  return requests.reduce((sum, r) => sum + (getValue(r) || 0), 0);
}

// ============================================================================
// Age Utilities
// ============================================================================

/**
 * Extract valid (non-null) request ages.
 *
 * req_age is null when req_entry or req_completion is missing or unparseable.
 * This filters to only requests with computable ages.
 *
 * @param requests - Request array
 * @returns Array of valid ages
 */
export function validReqAges(requests: Request[]): number[] {
  return requests.map(r => r.req_age).filter((a): a is number => a !== null);
}

/**
 * Check if a request is aged beyond the threshold.
 *
 * @param request - Request to check
 * @param threshold - Age threshold in days (default: AGE_HIGH_THRESHOLD)
 * @returns True if req_age exceeds threshold
 */
export function isReqAged(request: Request, threshold: number = AGE_HIGH_THRESHOLD): boolean {
  return request.req_age !== null && request.req_age > threshold;
}

/**
 * Age severity level for UI styling.
 */
export type AgeSeverity = 'high' | 'medium' | 'none';

/**
 * Determine age severity for a request.
 *
 * @param age - Request age in days (or null)
 * @returns Severity level
 */
export function ageSeverity(age: number | null): AgeSeverity {
  if (age === null) return 'none';
  if (age > AGE_HIGH_THRESHOLD) return 'high';
  if (age > AGE_MEDIUM_THRESHOLD) return 'medium';
  return 'none';
}

/**
 * Format age for display.
 *
 * @param age - Age in days (or null)
 * @returns Formatted string (e.g., "30d" or "—")
 */
export function formatReqAge(age: number | null): string {
  return age !== null ? `${age}d` : '—';
}

/**
 * Compare requests by age (descending).
 * Null ages sort last.
 *
 * @param a - First request
 * @param b - Second request
 * @returns Comparison result
 */
export function compareReqAgeDesc(a: Request, b: Request): number {
  return (b.req_age ?? -1) - (a.req_age ?? -1);
}

// ============================================================================
// Solution Consultant (SC) Name Utilities
// ============================================================================

/**
 * Split comma-separated SC names into individual names.
 *
 * A request can be assigned to multiple SCs, represented as a comma-separated
 * string. Each name is treated as a separate SC for grouping/counting.
 *
 * @param scNames - Comma-separated SC names (or null/undefined)
 * @returns Array of individual SC names, or ['No Value'] if empty
 */
export function splitUserSCNames(scNames: string | null | undefined): string[] {
  if (!scNames) return ['No Value'];
  const names = scNames.split(',').map(s => s.trim()).filter(Boolean);
  return names.length > 0 ? names : ['No Value'];
}

// ============================================================================
// URL Generation
// ============================================================================

/**
 * Generate Workfront URL for a request (Issue).
 *
 * @param request - Request record
 * @returns Workfront issue URL or '#' if req_id is missing
 */
export function getRequestURL(request: Request): string {
  return request.req_id
    ? `https://experience.adobe.com/#/@wfadoberm/so:adoberm-Production/workfront/issue/${request.req_id}`
    : '#';
}

/**
 * Generate Workfront URL for an opportunity (Project).
 *
 * @param request - Request record (contains opp_id)
 * @returns Workfront project URL or '#' if opp_id is missing
 */
export function getOpportunityURL(request: Request): string {
  return request.opp_id
    ? `https://experience.adobe.com/#/@wfadoberm/so:adoberm-Production/workfront/project/${request.opp_id}`
    : '#';
}
